/**
 * WebSmOrder — the mission orders screen (ported from Glow's WebSmOrder_type.jsx).
 *
 * Options: Lines (one row each; "* " headline, "_ " italic kicker, "- "/"> "
 *   bullet; `**word**` anywhere bounces that word in letter by letter)
 *   / Lead / BoxBody ("1" sets the copy under the headline in a panel)
 *   / LabelForCLICK1 / LabelForCLICK2
 *   / BgColor / AccentColor / ButtonTextColor
 * Emits CLICK1 (accept) and CLICK2 (decline).
 */
import { useEffect, useState, type ReactNode } from "react";
import {
  CHOICE_BTN_CLASS,
  FLY_IN_CLASS,
  SECTION_CLASS,
  stepVars,
} from "./step-shell";
import type { StepTypeProps } from "./types";

type LineKind = "strong" | "em" | "bullet" | "normal";

const parseLines = (raw: string): { kind: LineKind; text: string }[] =>
  String(raw || "")
    .split("\n")
    .map((l) => l.replace(/\s+$/, ""))
    .filter((l) => l.trim() !== "")
    .map((l) => {
      if (l.startsWith("* ")) return { kind: "strong", text: l.slice(2) };
      if (l.startsWith("_ ")) return { kind: "em", text: l.slice(2) };
      if (l.startsWith("- ") || l.startsWith("> "))
        return { kind: "bullet", text: l.slice(2) };
      return { kind: "normal", text: l };
    });

/**
 * `**word**` bounces in a letter at a time. It is one word's worth of
 * emphasis, so it is done with spans and a stagger rather than by animating
 * the whole line — the point is that the letters land separately.
 */
const EMPHASIS = /\*\*(.+?)\*\*/;

function renderLine(text: string): ReactNode {
  const m = text.match(EMPHASIS);
  if (!m) return text;
  const at = m.index ?? 0;
  return (
    <>
      {text.slice(0, at)}
      {/* uppercased: the bounce lands harder on caps, and it reads as a
          stamped word rather than an italic one */}
      <span className="tracking-[.04em] whitespace-nowrap">
        {[...m[1].toUpperCase()].map((ch, i) => (
          <span
            key={`${ch}-${i}`}
            style={{ animationDelay: `${220 + i * 55}ms` }}
            className="sm-bounce inline-block animate-[smLetterIn_.52s_cubic-bezier(.2,1.7,.45,1)_both]"
          >
            {ch === " " ? "\u00a0" : ch}
          </span>
        ))}
      </span>
      {text.slice(at + m[0].length)}
    </>
  );
}

const LINE_CLASS: Record<LineKind, string> = {
  strong:
    "text-center mb-4 text-[21px] leading-[1.32] font-bold text-[#f4eddb]",
  em: "text-center mt-4 text-[16px] leading-[1.5] text-[#a99d86] italic",
  bullet:
    "text-left mx-auto mt-2.5 max-w-[340px] text-[15.5px] leading-[1.5] text-[#e2d6bd]",
  normal: "text-center my-[7px] text-[17px] leading-[1.55] text-[#ddd3bf]",
};

export default function WebSmOrderType({ getOpt, dispatch }: StepTypeProps) {
  const bg = getOpt("BgColor") || "#0a0809";
  const accent = getOpt("AccentColor") || "#d6a94e";
  const btnText = getOpt("ButtonTextColor") || "#e6d3a4";

  const lines = parseLines(
    getOpt("Lines") ||
      "There are more of us than you'd guess.\n" +
        "But nobody just joins. You earn your way in if you're good enough.\n" +
        "_ Your first secret mission begins now:",
  );
  // The copy under the headline reads as a briefing on some steps — a panel
  // separates it from the headline above and the lead below.
  const boxBody = getOpt("BoxBody") === "1";
  const label1 = getOpt("LabelForCLICK1") || "I'm in.";
  const label2 = getOpt("LabelForCLICK2") || "";

  // Reveal sequence: the first two lines are there at once, then the rest flies
  // in on a card from the right, then the buttons pop.
  const firstTwo = lines.slice(0, 2);
  // the lead flies in on the card — from a dedicated `Lead` option, else any
  // lines beyond the first two
  const lead = getOpt("Lead");
  const rest = lead ? [{ kind: "em" as LineKind, text: lead }] : lines.slice(2);
  // Reveal order: headline is there on arrival, then the briefing panel slides
  // in, then the lead, then the buttons — the same right-to-left entrance the
  // intro step uses.
  const [showBox, setShowBox] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  // With no card to fly in, the buttons follow the headline straight away.
  const hasCard = rest.length > 0;
  useEffect(() => {
    const a = setTimeout(() => setShowBox(true), 250);
    const b = setTimeout(() => setShowCard(true), hasCard ? 550 : 250);
    return () => {
      clearTimeout(a);
      clearTimeout(b);
    };
  }, [hasCard]);
  useEffect(() => {
    if (!showCard) return;
    const t = setTimeout(() => setShowButtons(true), hasCard ? 350 : 150);
    return () => clearTimeout(t);
  }, [showCard, hasCard]);

  return (
    <section
      style={stepVars({ accent, bg, btnText })}
      className={`${SECTION_CLASS} px-4 py-[clamp(8px,3dvh,56px)]`}
    >
      <div className="relative z-[2] mx-auto w-full max-w-[432px] px-[26px] pt-[clamp(4px,2dvh,30px)] pb-[clamp(4px,2dvh,26px)] text-center">
        {firstTwo.map((ln, i) => {
          const line = (
            <div className={LINE_CLASS[ln.kind]}>
              {ln.kind === "bullet" ? (
                <>▸&nbsp; {renderLine(ln.text)}</>
              ) : (
                renderLine(ln.text)
              )}
            </div>
          );
          return boxBody && ln.kind !== "strong" ? (
            <div
              key={`lead-${i}`}
              className={`mx-auto mt-1 rounded-md border border-[var(--sm-accent-edge)] bg-[rgb(18_16_18/0.9)] px-5 py-4 shadow-[0_16px_44px_rgba(0,0,0,.5)] ${FLY_IN_CLASS} ${
                showBox
                  ? "translate-x-0 opacity-100"
                  : "translate-x-[120%] opacity-0"
              }`}
            >
              {line}
            </div>
          ) : (
            <div key={`lead-${i}`}>{line}</div>
          );
        })}

        {/* the lead flies in on its own, carried by size and light */}
        {rest.length ? (
          <div
            className={`mt-[clamp(10px,3dvh,24px)] ${FLY_IN_CLASS} ${
              showCard
                ? "translate-x-0 opacity-100"
                : "translate-x-[120%] opacity-0"
            }`}
          >
            {rest.map((ln) => (
              <div
                key={`${ln.kind}-${ln.text}`}
                className={
                  ln.kind === "em"
                    ? "text-center text-[clamp(19px,5.2cqw,23px)] leading-[1.3] font-medium tracking-[-.01em] text-balance text-[#f0e6d2]"
                    : LINE_CLASS[ln.kind]
                }
              >
                {ln.kind === "bullet" ? (
                  <>▸&nbsp; {renderLine(ln.text)}</>
                ) : (
                  renderLine(ln.text)
                )}
              </div>
            ))}
          </div>
        ) : null}

        {/* buttons — pop in after the card */}
        <div
          className={`mt-[clamp(12px,3dvh,26px)] flex flex-col items-stretch gap-[11px] sm:flex-row sm:flex-wrap sm:justify-center ${FLY_IN_CLASS} ${
            showButtons
              ? "translate-x-0 opacity-100"
              : "pointer-events-none translate-x-[120%] opacity-0"
          }`}
        >
          <button
            type="button"
            className={CHOICE_BTN_CLASS}
            onClick={() => {
              dispatch("CLICK1");
            }}
          >
            {label1}
          </button>
          {label2 ? (
            <button
              type="button"
              className={CHOICE_BTN_CLASS}
              onClick={() => dispatch("CLICK2")}
            >
              {label2}
            </button>
          ) : null}
        </div>
      </div>

      {/* No underscore in the name: Tailwind reads `_` in an arbitrary value
          as a space, so `sm_letterIn` would never match this keyframe. */}
      <style>{`
        @keyframes smLetterIn{0%{opacity:0;transform:translateY(-18px) scale(.8)}60%{opacity:1}100%{opacity:1;transform:none}}
        @media (prefers-reduced-motion:reduce){.sm-bounce{animation:none!important}}
      `}</style>
    </section>
  );
}
